import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { safeHref } from "../components/Field";

type Src = { field: string; source: { id: string; url: string; title: string | null; publisher: string | null; retrieved_at: string; http_status: number | null } };
type V = { status: string; pendingFields: string[]; note: string | null; verifiedAt: string | null; nextReviewAt: string | null };

export function SourcesPanel({ entityType, entityId }: { entityType: string; entityId: string }) {
  const qc = useQueryClient();
  const key = ["sources", entityType, entityId];
  const q = useQuery({ queryKey: key, queryFn: () => api<{ sources: Src[]; verification: V; factFields: string[] }>(`/api/admin/sources/${entityType}/${entityId}`) });
  const [form, setForm] = useState({ url: "", title: "", publisher: "", field: "*" });
  const [ver, setVer] = useState<{ status: string; pendingFields: string[]; note: string } | null>(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["queue"] });
    for (const k of ["program", "programs", "department", "problem", "problems", "problem-raw", "search", "suggest"]) qc.invalidateQueries({ queryKey: [k] }); };

  const add = useMutation({
    mutationFn: async () => {
      const { id } = await api<{ id: string }>("/api/admin/sources", { method: "POST",
        body: { url: form.url, title: form.title || undefined, publisher: form.publisher || undefined } });
      await api("/api/admin/fact-sources", { method: "POST", body: { entityType, entityId, field: form.field, sourceId: id } });
    },
    onSuccess: () => { setForm({ url: "", title: "", publisher: "", field: "*" }); refresh(); },
  });
  const unlink = useMutation({
    mutationFn: (s: Src) => api("/api/admin/fact-sources", { method: "DELETE", body: { entityType, entityId, field: s.field, sourceId: s.source.id } }),
    onSuccess: refresh,
  });
  const setV = useMutation({
    mutationFn: (v: { status: string; pendingFields: string[]; note: string }) =>
      api(`/api/admin/verification/${entityType}/${entityId}`, { method: "POST", body: { ...v, note: v.note || undefined } }),
    onSuccess: () => { setVer(null); refresh(); },
  });

  if (!q.data) return <p className="text-sm text-slate-500">Loading…</p>;
  const factFields = q.data.factFields;
  const v = ver ?? { status: q.data.verification.status, pendingFields: q.data.verification.pendingFields, note: q.data.verification.note ?? "" };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="mb-2 text-sm font-semibold">Sources</h3>
        {q.data.sources.length === 0 && <p className="text-sm text-slate-500">No sources yet. A record cannot be marked verified without one.</p>}
        <ul className="flex flex-col gap-1 text-sm">{q.data.sources.map((s) => (
          <li key={s.field + s.source.id} className="flex items-center justify-between gap-2">
            <span className="min-w-0 truncate"><code className="mr-2 text-xs">{s.field === "*" ? "whole record" : s.field}</code>
              {safeHref(s.source.url) ? <a href={safeHref(s.source.url)!} target="_blank" rel="noreferrer noopener" className="text-brand-700 hover:underline">{s.source.title ?? s.source.url}</a> : <span>{s.source.title ?? s.source.url}</span>}
              {s.source.http_status !== null && <span className="ml-2 text-xs text-slate-500">HTTP {s.source.http_status}</span>}</span>
            <button className="btn-ghost text-xs text-red-600" onClick={() => unlink.mutate(s)}>Unlink</button>
          </li>
        ))}</ul>
        {unlink.error && <p role="alert" className="mt-1 text-sm text-red-600">{(unlink.error as Error).message}</p>}
        <form className="mt-3 grid gap-2 sm:grid-cols-4" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
          <input className="input sm:col-span-2" type="url" required placeholder="https://… official page" aria-label="Source URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <input className="input" placeholder="Title" aria-label="Source title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <select className="input" aria-label="Backs field" value={form.field} onChange={(e) => setForm({ ...form, field: e.target.value })}>
            <option value="*">Whole record</option>{factFields.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <button className="btn-primary sm:col-span-4 sm:w-fit" disabled={add.isPending}>Add source</button>
          {add.error && <span role="alert" className="text-sm text-red-600 sm:col-span-4">{(add.error as Error).message}</span>}
        </form>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold">Verification</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <div><label className="label" htmlFor="vstatus">Status</label>
            <select id="vstatus" className="input" value={v.status} onChange={(e) => setVer({ ...v, status: e.target.value })}>
              <option value="verified">Verified</option><option value="pending">Pending</option>
              <option value="needs_review">Needs review</option><option value="stale">Stale</option>
            </select></div>
          <div><label className="label" htmlFor="vnote">Note</label>
            <input id="vnote" className="input" value={v.note} onChange={(e) => setVer({ ...v, note: e.target.value })} /></div>
        </div>
        <fieldset className="mt-2"><legend className="label">Fields still unverified (hidden from reps)</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">{factFields.map((f) => (
            <label key={f} className="flex items-center gap-1 text-xs"><input type="checkbox" checked={v.pendingFields.includes(f)}
              onChange={(e) => setVer({ ...v, pendingFields: e.target.checked ? [...v.pendingFields, f] : v.pendingFields.filter((x) => x !== f) })} />{f}</label>
          ))}</div>
        </fieldset>
        <button className="btn-primary mt-3" onClick={() => setV.mutate(v)} disabled={setV.isPending}>Save verification</button>
        {setV.error && <span role="alert" className="ml-3 text-sm text-red-600">{(setV.error as Error).message}</span>}
      </div>
    </div>
  );
}
